import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BLOB_URL } from "../shared/src";
import { catchError, forkJoin, map, Observable, of, shareReplay } from "rxjs";


@Injectable({
    providedIn: 'root' // This makes the service available application-wide
})
export class BlobService {
    private readonly BLOB_API = `${BLOB_URL}`;
    private readonly readUrlCache = new Map<string, string>();
    private readonly readUrlInFlight = new Map<string, Observable<string>>();
    private readonly azureBlobUrlRegex = /^https?:\/\/.+\.blob\.core\.windows\.net\//i;
    constructor(private http: HttpClient) { }

    public fetchImage(blobUrl: string) {

        const url = `${this.BLOB_API}/getFile?blobUrl=${encodeURIComponent(blobUrl)}`;
        const headers = new HttpHeaders().set('isBlob', 'true');
        return this.http.get(url, { headers, responseType: 'blob' });
    }

    /**
     * Streams private media through the API using the storage account
     * credentials. Works even when the stored URL names the wrong container,
     * and avoids SAS and CORS entirely.
     */
    public fetchMediaFile(blobUrl: string): Observable<Blob> {
        const url = `${this.BLOB_API}/media?blobUrl=${encodeURIComponent(blobUrl.split('?')[0])}`;
        const headers = new HttpHeaders().set('isBlob', 'true');
        return this.http.get(url, { headers, responseType: 'blob' });
    }

    public peekCachedReadSasUrl(blobUrl: string): string | undefined {
        const cached = this.readUrlCache.get(blobUrl);
        if (cached && cached.includes('rscd=inline')) {
            return cached;
        }
        return undefined;
    }

    /**
     * Resolve many Azure blob URLs to SAS URLs in one HTTP request (max 100 per call).
     * Populates the in-memory cache used by generateBlobReadSasUrl.
     */
    public generateBlobReadSasUrlBatch(
        blobUrls: string[],
        expiresInMinutes: number = 60,
    ): Observable<Map<string, string>> {
        const unique = [
            ...new Set(
                (blobUrls || []).filter(
                    (u) => typeof u === 'string' && this.isAzureBlobUrl(u),
                ),
            ),
        ];
        const uncached = unique.filter((u) => !this.peekCachedReadSasUrl(u));
        const resultMap = new Map<string, string>();
        unique.forEach((u) => {
            const hit = this.peekCachedReadSasUrl(u);
            if (hit) {
                resultMap.set(u, hit);
            }
        });
        if (uncached.length === 0) {
            return of(resultMap);
        }

        const chunkSize = 100;
        const chunks: string[][] = [];
        for (let i = 0; i < uncached.length; i += chunkSize) {
            chunks.push(uncached.slice(i, i + chunkSize));
        }

        const url = `${this.BLOB_API}/generate-blob-read-sas-batch`;
        const requests = chunks.map((chunk) =>
            this.http.post<{ result?: Array<{ blobUrl: string; sasUrl: string }> }>(url, {
                blobUrls: chunk,
                expiresInMinutes,
            }),
        );

        return forkJoin(requests).pipe(
            map((responses) => {
                responses.forEach((res) => {
                    const items = res?.result ?? [];
                    items.forEach(({ blobUrl, sasUrl }) => {
                        if (blobUrl && sasUrl) {
                            this.readUrlCache.set(blobUrl, sasUrl);
                            resultMap.set(blobUrl, sasUrl);
                        }
                    });
                });
                return resultMap;
            }),
            catchError(() => of(resultMap)),
        );
    }

    public generateBlobReadSasUrl(
        blobUrl: string,
        expiresInMinutes: number = 60,
        resolveMissing: boolean = false,
    ): Observable<string> {
        const cached = this.readUrlCache.get(blobUrl);
        if (cached && cached.includes('rscd=inline')) {
            return of(cached);
        }

        const inFlightKey = resolveMissing ? `${blobUrl}|resolve` : blobUrl;
        const existing = this.readUrlInFlight.get(inFlightKey);
        if (existing) {
            return existing;
        }

        let containerName = '';
        let blobName = '';

        try {
            const parsed = new URL(blobUrl);
            const path = decodeURIComponent(parsed.pathname || '').replace(/^\/+/, '');
            const [c, ...rest] = path.split('/').filter(Boolean);
            containerName = c || '';
            blobName = rest.join('/');
        } catch {
            const trimmed = (blobUrl || '').trim();
            const pathOnly = trimmed.replace(/^\/+/, '').split('?')[0] || '';
            const [c, ...rest] = pathOnly.split('/').filter(Boolean);
            containerName = c || '';
            blobName = rest.join('/');
        }

        const url = `${this.BLOB_API}/generate-blob-read-sas-by-name`;
        const req$ = this.http.get<any>(url, {
            params: {
                containerName,
                blobName,
                expiresInMinutes: String(expiresInMinutes),
                ...(resolveMissing ? { resolveMissing: 'true' } : {}),
            },
        }).pipe(
            map((res) => res?.result ?? ''),
            map((sasUrl: string) => {
                if (sasUrl && typeof sasUrl === 'string') {
                    this.readUrlCache.set(blobUrl, sasUrl);
                }
                return sasUrl;
            }),
            shareReplay(1),
        );

        this.readUrlInFlight.set(inFlightKey, req$);
        req$.subscribe({
            next: () => this.readUrlInFlight.delete(inFlightKey),
            error: () => this.readUrlInFlight.delete(inFlightKey),
        });

        return req$;
    }

    public isAzureBlobUrl(url: string | null | undefined): boolean {
        return typeof url === 'string' && this.azureBlobUrlRegex.test(url);
    }

    /**
     * Returns a SAS URL when the input is an Azure Blob URL.
     * For non-blob URLs (or errors), returns the original URL.
     */
    public toReadSasUrlIfBlob(
        url: string | null | undefined,
        expiresInMinutes: number = 60,
        resolveMissing: boolean = false,
    ): Observable<string> {
        if (!url || typeof url !== 'string') {
            return of('');
        }
        if (!this.isAzureBlobUrl(url)) {
            return of(url);
        }
        return this.generateBlobReadSasUrl(url, expiresInMinutes, resolveMissing).pipe(
            map((sas) => (sas && typeof sas === 'string' ? sas : url)),
            catchError(() => of(url)),
        );
    }

    /**
     * Upgrades Azure Blob URLs to SAS URLs in-place.
     * Uses BlobService caching/in-flight dedupe.
     */
    public upgradeBlobUrlsInPlace<T>(
        items: T[],
        getUrl: (item: T) => string | null | undefined,
        setUrl: (item: T, newUrl: string) => void,
        expiresInMinutes: number = 60,
    ): void {
        if (!Array.isArray(items) || items.length === 0) return;
        items.forEach((item) => {
            const original = getUrl(item);
            if (!this.isAzureBlobUrl(original)) return;
            this.toReadSasUrlIfBlob(original, expiresInMinutes).subscribe((sas) => {
                if (sas && sas !== original) {
                    // Best-effort: if item changed reference, caller still owns consistency.
                    setUrl(item, sas);
                }
            });
        });
    }
}
